import {
  createUserWithEmailAndPassword as fbCreateUser,
  deleteUser,
  getAdditionalUserInfo,
  onAuthStateChanged as fbOnAuthStateChanged,
  sendPasswordResetEmail as fbSendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup as fbSignInWithPopup,
  signOut,
  updateProfile
} from "firebase/auth";
import type { User, UserCredential } from "firebase/auth";
import { firebaseAuth, firebaseGoogleAuth } from "../utils/firebase-config";

/** A credential plus the additionalUserInfo the v8 SDK used to inline. */
export type AuthResult = UserCredential & {
  additionalUserInfo: ReturnType<typeof getAdditionalUserInfo>;
};

// The v8 SDK put additionalUserInfo on the credential itself; the modular SDK
// exposes it through a helper instead. processUser() still reads
// result.additionalUserInfo, so re-attach it on every path that feeds into it.
function withAdditionalUserInfo(result: UserCredential): AuthResult {
  return {
    ...result,
    user: result.user,
    additionalUserInfo: getAdditionalUserInfo(result)
  };
}

export function loginWithEmailAndPassword(
  email: string,
  password: string
): Promise<AuthResult> {
  return signInWithEmailAndPassword(firebaseAuth, email, password).then(
    withAdditionalUserInfo
  );
}

export function signOutOfApp() {
  return signOut(firebaseAuth);
}

export function createUserWithEmailAndPassword(
  email: string,
  password: string
): Promise<AuthResult> {
  return fbCreateUser(firebaseAuth, email, password).then(
    withAdditionalUserInfo
  );
}

export function onAuthStateChanged(callback: (user: User | null) => void) {
  return fbOnAuthStateChanged(firebaseAuth, callback);
}

export function signInWithPopup(): Promise<AuthResult> {
  return fbSignInWithPopup(firebaseAuth, firebaseGoogleAuth).then(
    withAdditionalUserInfo
  );
}

export function sendPasswordResetEmail(email: string): Promise<void> {
  return fbSendPasswordResetEmail(firebaseAuth, email);
}

export function deleteAccount(): Promise<void> {
  const { currentUser } = firebaseAuth;
  if (!currentUser) {
    return Promise.reject(new Error("No signed-in user to delete."));
  }
  return deleteUser(currentUser);
}

export function updateUserDisplayName(user: User, displayName: string) {
  return updateProfile(user, { displayName });
}
